/**
 * Block 13 PR-G Phase 1 — feature-local wrapper над `contracts` store
 * в `pomoshnik-offline-v1` (ADR-0025 §Q3).
 *
 * **Дизайн (mirror Block 12 `savedQuotesIdb.ts` pattern):** existing
 * `lib/offline/idb/contracts.ts` остаётся generic (data: ContractDraft —
 * typed at lib layer). Этот модуль добавляет feature-domain surface:
 * `SavedContract` (с auto-counter name + draftKey alias) и
 * `saveContractWithState` / `loadSavedContract` / `listSavedContracts` /
 * `deleteSavedContract`. Compiler-narrowed type-safety без circular dep
 * features → lib.
 *
 * **Schema mapping ContractRecord ↔ SavedContract:**
 *   - ContractRecord.id ↔ SavedContract.id (UUID)
 *   - ContractRecord.userId ↔ SavedContract.userId
 *   - ContractRecord.data ↔ SavedContract.state (ContractDraft)
 *   - ContractRecord.pdfBlobKey: null (Phase 1 — no R2 backend)
 *   - ContractRecord.syncStatus: 'local' (Phase 1 — always)
 *   - ContractRecord — NEW field `name` added через type augmentation
 *     (IDB structured clone allows extra JSON-safe fields).
 *
 * **Transient PII (P-009):** `SavedContract.state.customerRequisites[].value` —
 * чувствительные данные клиента (ИИН/БИН/паспорт/адрес/телефон). PR-G Phase 7
 * реализует `clearCustomerInSavedContract(contractId)` ниже — wrapper над
 * load → mutate → save. Triggers — после Download/Share PDF с экрана
 * /contracts/:id/pdf (saved snapshot уже в IDB; reducer dispatch не помогает,
 * нужен IDB overwrite напрямую). Executor side НЕ trogается.
 *
 * References:
 *   - ADR-0025 §Q3 (contracts store rationale)
 *   - Reference pattern: `features/calculator/quote/state/savedQuotesIdb.ts`
 *     (Block 12 PR-D-A Phase D5 Step 10)
 */

import type { ContractDraft } from '@pomoshnik/shared';
import type { ContractRecord } from '@/lib/offline/idb/database';
import {
  saveContract as saveContractRaw,
  getContract as getContractRaw,
  deleteContract as deleteContractRaw,
  listAllContracts as listAllContractsRaw,
  listContractsByUser as listContractsByUserRaw,
} from '@/lib/offline/idb/contracts';

/**
 * Domain-typed запись сохранённого договора. `state` поле — snapshot полного
 * ContractDraft; `name` — для list-view на Экране 2.13.1.
 */
export interface SavedContract {
  id: string;
  /** userId владельца (для filter by user в multi-user IDB future). */
  userId: string;
  /** Auto-generated «Договор № {N} от {date}» (Phase 5 Экран 2.13.1 list). */
  name: string;
  /** Snapshot ContractDraft на момент save. */
  state: ContractDraft;
  createdAt: number;
  updatedAt: number;
}

/**
 * Расширенный ContractRecord — type cast в storage. IDB structured clone
 * сохраняет любые JSON-safe поля без schema enforcement, поэтому добавление
 * `name` поверх существующего interface — runtime-safe (mirror SavedQuoteRecord
 * pattern из Block 12).
 */
type SavedContractRecord = ContractRecord & { name: string };

function toRecord(saved: SavedContract): SavedContractRecord {
  return {
    id: saved.id,
    userId: saved.userId,
    data: saved.state,
    name: saved.name,
    pdfBlobKey: null,
    syncStatus: 'local',
    createdAt: saved.createdAt,
    updatedAt: saved.updatedAt,
  };
}

function fromRecord(record: ContractRecord): SavedContract {
  const named = record as SavedContractRecord;
  // Fallback на пустую строку (i18n-safe — Phase 5 list UI рендерит «—» либо
  // auto-generated "Договор № {N}" derived из state.contractNum + contractDate
  // на стороне UI с учётом active locale per template.meta.lang). См. B-149.
  return {
    id: named.id,
    userId: named.userId,
    name: named.name ?? '',
    state: named.data,
    createdAt: named.createdAt,
    updatedAt: named.updatedAt,
  };
}

export async function saveContractWithState(saved: SavedContract): Promise<void> {
  await saveContractRaw(toRecord(saved));
}

export async function loadSavedContract(id: string): Promise<SavedContract | undefined> {
  const record = await getContractRaw(id);
  if (!record) return undefined;
  return fromRecord(record);
}

export async function listSavedContracts(): Promise<SavedContract[]> {
  const records = await listAllContractsRaw();
  return records.map(fromRecord);
}

export async function listSavedContractsByUser(userId: string): Promise<SavedContract[]> {
  const records = await listContractsByUserRaw(userId);
  return records.map(fromRecord);
}

export async function deleteSavedContract(id: string): Promise<void> {
  await deleteContractRaw(id);
}

/**
 * PR-C1 (ADR-0035) Phase 1 — forward-chain lookup «последний договор по КП».
 *
 * Связь договор↔КП пишется при создании договора из КП:
 * `quoteToContractMapping.ts:207` → `state.fromQuoteId = quote.id`. Эта функция
 * находит СОХРАНЁННЫЕ договоры с matching `fromQuoteId` и возвращает id того,
 * у которого максимальный `updatedAt` — «последний редактированный» (решение #2
 * Aibek). Несохранённый черновик (Save не нажат) не попадёт в IDB → не найдётся,
 * кнопка «Вперёд» на КП скрыта (решение #1).
 *
 * Возвращает `null`, если ни одного сохранённого договора по этому КП нет.
 * Чистое чтение IDB (R2). Linear scan через `listSavedContracts()` — для
 * текущего масштаба (десятки договоров, single-user) приемлемо; при росте —
 * IDB-индекс по `fromQuoteId` (mirror B-114 для quotes).
 */
export async function findLatestContractIdByQuoteId(quoteId: string): Promise<string | null> {
  if (!quoteId) return null;
  const all = await listSavedContracts();
  const matching = all.filter((c) => c.state.fromQuoteId === quoteId);
  if (matching.length === 0) return null;
  const latest = matching.reduce((acc, c) => (c.updatedAt > acc.updatedAt ? c : acc));
  return latest.id;
}

/**
 * Phase 7 hotfix round 5 (R5-#1, 2026-05-26) — uniqueness check для contractNum.
 *
 * Aibek visual verify round 10 finding #1: 2 разных договора не могут иметь
 * одинаковый номер. SavedContract.name теперь uses state.contractNum first
 * (см. BottomActions.handleSave), и uniqueness нужно validate перед save.
 *
 * Returns true если найден другой SavedContract с таким же contractNum
 * (исключая excludeId — обычно current draft.id чтобы re-save того же
 * договора не давал false positive).
 *
 * MUST: trim-normalized, case-sensitive compare. Empty contractNum не
 * collide'ит — fallback на counter (см. getNextContractCounter) гарантирует
 * uniqueness через atomic increment.
 */
export async function hasContractNumConflict(
  contractNum: string,
  excludeId: string,
): Promise<boolean> {
  const trimmed = contractNum.trim();
  if (trimmed === '') return false;
  const all = await listSavedContracts();
  return all.some((c) => c.id !== excludeId && c.state.contractNum.trim() === trimmed);
}

/**
 * PR-G Phase 7 (P-009 transient PII clear) — load saved contract by id, очистить
 * `customerRequisites[].value` (preserve label/key/removable), overwrite IDB record.
 *
 * Triggers (from `ContractPdfPreviewContainer`):
 *   - handleDownload после `triggerContractPdfDownload(blob, filename)` resolved
 *   - onResult от `ShareContractBottomSheet` для status ∈ {shared, downloaded, desktop-direct}
 *
 * Не triggers:
 *   - Если record отсутствует (no-op, не throw — Download/Share с unloaded state
 *     невозможен но guard remains defensive).
 *   - Если все values уже пустые (idempotent — no IDB write на повторный CLEAR).
 *
 * Executor side НЕ trogается (собственные реквизиты пользователя, не PII клиента).
 * updatedAt НЕ обновляется — это transient cleanup, не logical content change
 * (иначе sort by updatedAt на «Мои договоры» exposes private PDF action).
 */
export async function clearCustomerInSavedContract(id: string): Promise<void> {
  const record = await getContractRaw(id);
  if (!record) return;
  const draft = record.data;
  const allEmpty = draft.customerRequisites.every((r) => r.value === '');
  if (allEmpty) return;
  const cleared: ContractDraft = {
    ...draft,
    customerRequisites: draft.customerRequisites.map((r) => ({ ...r, value: '' })),
  };
  await saveContractRaw({
    ...record,
    data: cleared,
  });
}
